import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { PageHeader, LoadingShell } from '@/components/ui-bits';
import { Plus, Settings as Cog, GripVertical, Trash2, Search, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useGlossary } from '@/lib/glossary';

export default function PipelinePage() {
  const { t } = useGlossary();
  const [data, setData] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState('');
  const [editing, setEditing] = useState(false);
  const [newStage, setNewStage] = useState({ name: '', color: '#3b82f6' });
  const [newPipeName, setNewPipeName] = useState('');
  const [drag, setDrag] = useState(null);
  const [q, setQ] = useState('');

  const load = async (pid) => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    const id = pid || pipelineId;
    if (id) params.set('pipeline_id', id);
    const { data: d } = await api.get(`/pipeline/kanban?${params.toString()}`);
    setData(d);
    setPipelines(d.pipelines || []);
    if (d.pipeline?.id && !id) setPipelineId(d.pipeline.id);
  };

  useEffect(() => { load(); }, [q, pipelineId]);

  if (!data) return <LoadingShell />;

  const moveLead = async (leadId, stageId) => {
    try {
      await api.patch(`/leads/${leadId}`, { stage_id: stageId, pipeline_id: pipelineId || data.pipeline?.id });
      toast.success(`${t('lead')} movido`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const addStage = async () => {
    if (!newStage.name.trim()) return;
    try {
      const qs = pipelineId ? `?pipeline_id=${pipelineId}` : '';
      await api.post(`/pipeline/stages${qs}`, { name: newStage.name, color: newStage.color });
      setNewStage({ name: '', color: '#3b82f6' });
      toast.success('Etapa agregada');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const renameStage = async (s, name) => {
    try {
      await api.patch(`/pipeline/stages/${s.id}`, {
        name, color: s.color, sla_hours: s.sla_hours, order: s.order,
        is_won: !!s.is_won, is_lost: !!s.is_lost,
      });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const deleteStage = async (s) => {
    if (!confirm(`¿Borrar etapa "${s.name}"?`)) return;
    try {
      await api.delete(`/pipeline/stages/${s.id}`);
      toast.success('Eliminada');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const createPipeline = async () => {
    if (!newPipeName.trim()) return;
    try {
      const { data: res } = await api.post('/pipeline', { name: newPipeName.trim() });
      toast.success('Pipeline creado');
      setNewPipeName('');
      setPipelineId(res.pipeline.id);
      setEditing(true);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const setDefault = async (id) => {
    try {
      await api.patch(`/pipeline/${id}`, { is_default: true });
      toast.success('Pipeline por defecto actualizado');
      load(id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const deletePipeline = async (id, name) => {
    if (!confirm(`¿Borrar pipeline "${name}"? Debe estar vacío.`)) return;
    try {
      await api.delete(`/pipeline/${id}`);
      toast.success('Pipeline eliminado');
      setPipelineId('');
      load('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const toggleStageFlag = async (s, flag) => {
    const patch = {
      name: s.name, color: s.color, sla_hours: s.sla_hours, order: s.order,
      is_won: flag === 'is_won' ? !s.is_won : !!s.is_won,
      is_lost: flag === 'is_lost' ? !s.is_lost : !!s.is_lost,
    };
    if (flag === 'is_won' && patch.is_won) patch.is_lost = false;
    if (flag === 'is_lost' && patch.is_lost) patch.is_won = false;
    try {
      await api.patch(`/pipeline/stages/${s.id}`, patch);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  return (
    <div>
      <PageHeader
        title={t('pipeline')}
        description={`Multi-${t('pipeline').toLowerCase()}: elige embudo, configura etapas (Ganado/Perdido).`}
        actions={(
          <button data-testid="pipeline-config-toggle" onClick={() => setEditing((v) => !v)} className="btn-ghost">
            <Cog className="h-4 w-4" /> {editing ? 'Cerrar' : 'Configurar'}
          </button>
        )}
      />

      <div className="surface p-3 mb-4 flex flex-wrap items-center gap-2">
        <select
          data-testid="pipeline-select"
          className="input w-auto min-w-[200px]"
          value={pipelineId || data.pipeline?.id || ''}
          onChange={(e) => setPipelineId(e.target.value)}
        >
          {(pipelines.length ? pipelines : (data.pipeline ? [data.pipeline] : [])).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.is_default ? ' ★' : ''}
            </option>
          ))}
        </select>
        <label className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Buscar ${t('lead').toLowerCase()}…`} className="input pl-9" />
        </label>
      </div>

      {editing && (
        <div className="surface p-4 mb-4 space-y-4">
          <div>
            <h3 className="font-display font-semibold mb-2">Pipelines</h3>
            <div className="flex flex-wrap gap-2 mb-2">
              {pipelines.map((p) => (
                <div key={p.id} className={`inline-flex items-center gap-1 border rounded-[10px] px-2 py-1 text-sm ${p.id === (pipelineId || data.pipeline?.id) ? 'border-primary bg-primary/10' : 'border-border'}`}>
                  <button type="button" className="font-medium" onClick={() => setPipelineId(p.id)}>{p.name}</button>
                  {p.is_default ? (
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                  ) : (
                    <button type="button" title="Marcar por defecto" onClick={() => setDefault(p.id)} className="btn-ghost h-6 w-6 p-0">
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button type="button" onClick={() => deletePipeline(p.id, p.name)} className="btn-ghost h-6 w-6 p-0 text-[hsl(var(--danger))]">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newPipeName} onChange={(e) => setNewPipeName(e.target.value)} placeholder="Nuevo pipeline (ej. Soporte, Partners…)" className="input" />
              <button type="button" onClick={createPipeline} className="btn-primary"><Plus className="h-4 w-4" /> Crear</button>
            </div>
          </div>

          <div>
            <h3 className="font-display font-semibold mb-2">Etapas de este pipeline</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.stages.map((s) => (
                <div key={s.id} className="flex flex-col gap-1 border border-border rounded-[10px] p-2">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: s.color }} />
                    <input defaultValue={s.name} onBlur={(e) => e.target.value !== s.name && renameStage(s, e.target.value)} className="input flex-1" />
                    <input
                      type="color"
                      defaultValue={s.color}
                      onBlur={(e) => api.patch(`/pipeline/stages/${s.id}`, {
                        name: s.name, color: e.target.value, sla_hours: s.sla_hours, order: s.order,
                        is_won: !!s.is_won, is_lost: !!s.is_lost,
                      }).then(() => load())}
                      className="h-8 w-10 rounded"
                    />
                    <button data-testid="stage-delete-btn" onClick={() => deleteStage(s)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex gap-2 text-[11px]">
                    <button
                      type="button"
                      onClick={() => toggleStageFlag(s, 'is_won')}
                      className={`chip ${s.is_won ? 'bg-emerald-500/15 text-emerald-600' : ''}`}
                    >
                      Ganado
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleStageFlag(s, 'is_lost')}
                      className={`chip ${s.is_lost ? 'bg-rose-500/15 text-rose-600' : ''}`}
                    >
                      Perdido
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={newStage.name} onChange={(e) => setNewStage({ ...newStage, name: e.target.value })} placeholder="Nombre de etapa" className="input" />
              <input type="color" value={newStage.color} onChange={(e) => setNewStage({ ...newStage, color: e.target.value })} className="h-10 w-12 rounded" />
              <button data-testid="stage-add-btn" onClick={addStage} className="btn-primary"><Plus className="h-4 w-4" /> Agregar</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-3">
        {data.stages.map((s) => (
          <div
            key={s.id}
            data-testid="kanban-column"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drag && moveLead(drag, s.id)}
            className="w-[320px] shrink-0"
          >
            <div className="flex items-center justify-between mb-2 px-3 py-2 rounded-[10px] bg-secondary/60 border border-border">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                <span className="font-medium text-sm">{s.name}</span>
                {s.is_won && <span className="text-[10px] text-emerald-600">✓</span>}
                {s.is_lost && <span className="text-[10px] text-rose-600">✕</span>}
              </div>
              <span className="chip">{(data.leads_by_stage[s.id] || []).length}</span>
            </div>
            <div className="space-y-2 min-h-[200px]">
              {(data.leads_by_stage[s.id] || []).map((l) => (
                <Link
                  key={l.id}
                  to={`/app/leads/${l.id}`}
                  data-testid="kanban-card"
                  draggable
                  onDragStart={() => setDrag(l.id)}
                  onDragEnd={() => setDrag(null)}
                  className="block rounded-[12px] border border-border bg-card p-3 shadow-sm hover:border-primary/40 hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm truncate">{l.first_name} {l.last_name}</div>
                    <GripVertical className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{l.phone_e164 || l.email || '—'}</div>
                  {l.source && <div className="mt-2 inline-flex items-center chip">{l.source}</div>}
                </Link>
              ))}
              {(data.leads_by_stage[s.id] || []).length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-8 border border-dashed border-border rounded-[10px]">Sin {t('leads').toLowerCase()}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
